import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { fileLinkClick } from '../utils/openFile.js';

// Welkom: for a new function holder or volunteer — who to turn to, what to know, where
// things are, who the colleagues are. After 4 weeks: "Hoe gaat het?" for the contact person.

const fmtDate = v => (v ? new Date(`${v.replace(' ', 'T')}${v.includes('T') || v.length <= 10 ? '' : 'Z'}`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' }) : '');
const fmtWhen = v => (v ? new Date(v).toLocaleString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : '');
const MOODS = [['goed', '🙂', 'Goed'], ['gaat', '😐', 'Gaat wel'], ['lastig', '🙁', 'Lastig']];

function Person({ id, name, avatarUrl, sub }) {
  return (
    <a href={`/profile/${id}`} style="display:flex;gap:10px;align-items:center;text-decoration:none;color:inherit;min-width:0">
      {avatarUrl
        ? <img src={avatarUrl} alt="" style="width:40px;height:40px;border-radius:50%;object-fit:cover;flex-shrink:0" />
        : <div style="width:40px;height:40px;border-radius:50%;background:var(--color-primary);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;flex-shrink:0">{(name || '?').charAt(0).toUpperCase()}</div>}
      <div style="min-width:0">
        <div style="font-weight:600">{name || 'Oud-lid'}</div>
        {sub && <div class="text-sm text-muted" style="overflow-wrap:anywhere">{sub}</div>}
      </div>
    </a>
  );
}

export default function WelcomeView({ id }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [mood, setMood] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [answerError, setAnswerError] = useState(null);
  const load = () => api(`/api/welcome/${id}`).then(d => {
    setData(d);
    if (d.own && !d.seen_at) api(`/api/welcome/${id}/seen`, { method: 'POST' }).catch(() => {});
  }).catch(err => setError(err.code === 'not_found' ? 'Dit welkom bestaat niet of is niet voor jou.' : 'Het welkom kon niet worden geladen. Probeer het later opnieuw.'));
  useEffect(() => { setData(null); setMood(null); setText(''); load(); }, [id]);

  async function answer() {
    setBusy(true); setAnswerError(null);
    try { await api(`/api/welcome/${id}/checkin`, { method: 'POST', body: JSON.stringify({ mood, text }) }); await load(); } catch { setAnswerError('Versturen lukte niet. Probeer het opnieuw.'); }
    setBusy(false);
  }

  if (error) return <div class="alert alert-error">{error}</div>;
  if (!data) return <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>;
  const first = (data.user.name || '').split(' ')[0];
  const o = data.opening;
  const askOpen = data.own && data.checkin.sent_at && !data.checkin.answered_at;

  return (
    <div style="max-width:820px">
      <div style="margin-bottom:16px">
        <h1 class="page-title">{data.kind === 'klus' ? `🙌 Je helpt mee: ${data.label}` : `👋 Welkom als ${data.label}`}{!data.own && ` — ${data.user.name}`}</h1>
        <div class="text-sm text-muted">{data.place ? `${data.place} · ` : ''}sinds {fmtDate(data.created_at)}</div>
      </div>
      <div style="display:flex;flex-direction:column;gap:16px">
        {askOpen && (
          <Card title="Hoe gaat het?">
            <div class="text-sm text-muted" style="margin-bottom:8px">Je bent nu een paar weken bezig. {data.contact ? `${data.contact.name.split(' ')[0]} krijgt je antwoord.` : ''}</div>
            <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px">
              {MOODS.map(([k, emoji, label]) => (
                <Button key={k} size="sm" variant={mood === k ? 'primary' : 'secondary'} onClick={() => setMood(k)}>{emoji} {label}</Button>
              ))}
            </div>
            <textarea class="form-input" rows={2} maxLength={1000} value={text} onInput={e => setText(e.target.value)} placeholder="Wil je iets toelichten? (optioneel)" aria-label="Toelichting" />
            <div style="margin-top:8px;display:flex;gap:10px;align-items:center;flex-wrap:wrap">
              <Button onClick={answer} disabled={busy || !mood}>Versturen</Button>
              {answerError && <span class="text-sm" style="color:var(--color-danger)">{answerError}</span>}
            </div>
          </Card>
        )}
        {data.checkin.answered_at && data.checkin.mood && (
          <Card title="Check-in">
            <div style="font-size:14px">{MOODS.find(m => m[0] === data.checkin.mood)?.[1]} {MOODS.find(m => m[0] === data.checkin.mood)?.[2]} · {fmtDate(data.checkin.answered_at)}</div>
            {data.checkin.text && <div style="font-size:14px;white-space:pre-line;margin-top:4px;overflow-wrap:anywhere">{data.checkin.text}</div>}
          </Card>
        )}

        {data.message && (
          <Card title="💬 Welkom">
            <div style="white-space:pre-line;font-size:14px;overflow-wrap:anywhere">{data.message}</div>
          </Card>
        )}

        <Card title={data.own ? 'Je aanspreekpunt' : 'Aanspreekpunt'}>
          {data.contact
            ? <Person id={data.contact.id} name={data.contact.name} avatarUrl={data.contact.avatar_url} sub={data.contact.contact_for ? `Bij mij terecht voor: ${data.contact.contact_for}` : 'Stel gerust je vragen.'} />
            : <div class="text-sm text-muted">Er is nog geen aanspreekpunt. Vraag het bestuur of een ledenbeheerder wie je kan helpen.</div>}
          {!data.own && data.contact && <div class="text-sm text-muted" style="margin-top:6px">{first} ziet dit ook.</div>}
        </Card>

        {o && (
          <Card title={data.kind === 'klus' ? 'Wanneer en waar' : 'Over deze rol'}>
            <div style="display:flex;flex-direction:column;gap:6px;font-size:14px">
              {o.starts_at && <div>📅 {fmtWhen(o.starts_at)}</div>}
              {o.location && <div>📍 {o.location}</div>}
              {o.commitment && <div>⏱️ {o.commitment}</div>}
              {o.description && <div style="white-space:pre-line;overflow-wrap:anywhere">{o.description}</div>}
              {o.task_id && <a href={`/tasks/${o.task_id}`}>Naar je taak →</a>}
              {o.process_id && <a href={`/processes/${o.process_id}`}>Naar je inwerktraject →</a>}
            </div>
          </Card>
        )}

        {data.read_first.length > 0 && (
          <Card title="📖 Lees eerst">
            <div style="display:flex;flex-direction:column;gap:4px">
              {data.read_first.map(d => (
                <a key={d.id} href={d.is_external ? (d.external_url || '/documents') : `/api/documents/${d.id}/download`} target="_blank" rel="noopener noreferrer" style="font-size:14px;overflow-wrap:anywhere"
                  onClick={d.is_external ? undefined : fileLinkClick}>
                  {d.is_external ? '🔗' : '📄'} {d.name}
                </a>
              ))}
            </div>
          </Card>
        )}

        {data.notes.length > 0 && (
          <Card title="📝 Dit moet je weten">
            {data.notes.map((n, idx) => (
              <div key={n.id} style={`font-size:14px;margin-bottom:10px;${idx > 0 ? 'opacity:.75' : ''}`}>
                <div style="white-space:pre-line;overflow-wrap:anywhere">{n.note}</div>
                <div class="text-sm text-muted">{n.written_by_name || 'Onbekend'} · {fmtDate(n.created_at)}</div>
              </div>
            ))}
          </Card>
        )}

        {data.folders.length > 0 && (
          <Card title={data.own ? '📁 Je mappen' : '📁 Mappen'}>
            <div style="display:flex;flex-direction:column;gap:4px">
              {data.folders.map(f => <a key={f.id} href={`/documents?folder=${f.id}`} style="font-size:14px">📁 {f.name}</a>)}
            </div>
          </Card>
        )}

        {data.colleagues.length > 0 && (
          <Card title={data.own ? "👥 Je collega's" : "👥 Collega's"}>
            <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px">
              {data.colleagues.map(c => <Person key={c.id} id={c.id} name={c.display_name} avatarUrl={c.avatar_url} sub={c.functions} />)}
            </div>
          </Card>
        )}

        {data.tasks.length > 0 && (
          <Card title={data.own ? '✅ Je open taken' : '✅ Open taken'}>
            {data.tasks.map(t => (
              <div key={t.id} style="font-size:14px;padding:2px 0;overflow-wrap:anywhere">
                <a href={`/tasks/${t.id}`}>{t.title}</a>{t.deadline && <span class="text-sm text-muted"> · deadline {fmtDate(t.deadline.slice(0, 10))}</span>}
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}
